import { contourArea, edgeLengths, orderCorners } from "@webscan/polygon.js";
import type * as OrtNamespace from "onnxruntime-web";
import { disposeTensors, withOrtLock } from "./ort-lock";
import { GpuLetterbox } from "./letterbox-gpu";
import type { SourceWindow, ViewRect } from "../shared/geometry";
import type { PixelSource } from "./camera-frame";

/**
 * The learned corner regressor, as the browser's detection stage.
 *
 * This is the piece the first browser port never had. It ran the classical funnel alone
 * — Canny, contours, colour masks — which measures 3.1 % usable frames on
 * `clips/scan-40-60-720p.mp4` against 86.7 % for the pose head. One 10 MB graph is the
 * whole difference, so here detection is the model and only the model; the classical
 * sources are not wired in at all.
 *
 * Decoding follows `cardvision/vision/model.py::OnnxPoseDetector` exactly, because the
 * quads come back in the letterboxed raster's pixels and any disagreement about the
 * padding lands directly on the corners.
 */

/** A card is four corner keypoints. */
const KEYPOINTS = 4;

interface CapturedGpuIo {
  device: GPUDevice;
  inputBuffer: GPUBuffer;
  outputBuffer: GPUBuffer;
  readbackBuffer: GPUBuffer;
  inputTensor: OrtNamespace.Tensor;
  outputTensor: OrtNamespace.Tensor;
  outputData: Float32Array;
  outputBytes: number;
}

// Stable WebGPU bit flags also keep the CPU-only model tests independent of DOM globals.
const GPU_MAP_READ = 0x0001;
const GPU_COPY_SRC = 0x0004;
const GPU_COPY_DST = 0x0008;
const GPU_STORAGE = 0x0080;
const MAP_MODE_READ = 0x0001;
const align16 = (bytes: number) => Math.ceil(bytes / 16) * 16;

/**
 * Grey the letterbox is padded with — ultralytics' own value.
 *
 * The model was trained through their pipeline, so the bars have to look to it the way
 * they did then. Exported because the letterbox itself is now drawn by the caller.
 */
export { PAD_VALUE } from "./letterbox-canvas";

/**
 * A frame already fitted into the model's raster, with the mapping back.
 *
 * Producing this is the caller's job because a canvas can do it on the GPU in one
 * `drawImage`, where doing it here meant pulling the frame down, resampling it in scalar
 * JavaScript through a `Float64Array`, and pushing it back up — 7.6 MB of intermediate
 * per frame, which cost more than the inference it was feeding.
 */
export interface LetterboxedFrame {
  /** RGBA at model size, or empty when the input already resides on WebGPU. */
  data: Uint8ClampedArray;
  gpu?: boolean;
  /** Model pixels per source pixel. */
  scale: number;
  /** Where the image sits inside the padded raster. */
  left: number;
  top: number;
}

/** Skip the thousands of effectively-empty candidate rows before geometric decoding. */
const PREFILTER_SCORE = 1e-3;

/** `DetectionSettings.model_confidence` — the policy floor, not the padding floor. */
export const CONFIDENCE = 0.5;

/** One detection, in the coordinate space of the frame that was passed in. */
export interface PoseDetection {
  /** Flat `[x0,y0,...,x3,y3]`, ordered top-left, top-right, bottom-right, bottom-left. */
  quad: Float32Array;
  area: number;
  aspect: number;
  score: number;
  source: "model";
}

export class PoseDetector {
  private readonly session: OrtNamespace.InferenceSession;
  private readonly inputName: string;
  private readonly outputName: string;
  private readonly outputStride: number;
  private readonly keypointOffset: number;
  private readonly maxCandidates: number;
  private readonly gpuIo: CapturedGpuIo | null;
  private videoSampler: GpuLetterbox | null = null;
  readonly width: number;
  readonly height: number;
  /** Reused across frames — one 4.9 MB float buffer per call would dominate the loop. */
  private readonly input: Float32Array<ArrayBuffer>;
  /**
   * Cost of the last call, split.
   *
   * `pack` is CPU work over the readback; `infer` is the upload, dispatch and readback of
   * the graph itself. They have entirely different fixes, so lumping them under one
   * "detect" number makes the next decision unanswerable.
   */
  lastPackMs = 0;
  lastInferMs = 0;
  /** Measured serialized queue wait, excluded from the cadence's processing cost. */
  lastQueueMs = 0;

  private constructor(
    session: OrtNamespace.InferenceSession,
    inputName: string,
    outputName: string,
    width: number,
    height: number,
    outputStride: number,
    keypointOffset: number,
    maxCandidates: number,
    gpuIo: CapturedGpuIo | null,
  ) {
    this.session = session;
    this.inputName = inputName;
    this.outputName = outputName;
    this.width = width;
    this.height = height;
    this.outputStride = outputStride;
    this.keypointOffset = keypointOffset;
    this.maxCandidates = maxCandidates;
    this.gpuIo = gpuIo;
    this.input = new Float32Array(3 * width * height);
  }

  /**
   * Build a detector from an already-created session.
   *
   * The raster comes from the graph rather than from a constant: exporting at 416 instead
   * of 640 is the documented escape hatch when the CPU budget is tight, and this wrapper
   * should follow the file rather than have to be changed alongside it.
   */
  static async create(
    ort: typeof OrtNamespace,
    bytes: Uint8Array,
    executionProviders: string[],
    webglRaster?: number,
  ): Promise<PoseDetector> {
    const persistentGpuIo = executionProviders[0] === "webgpu";
    const webgl = executionProviders[0] === "webgl";
    const session = await ort.InferenceSession.create(bytes, {
      // Name exactly one provider. The caller selects the matching graph, and no fallback
      // may quietly introduce a per-frame boundary between two execution providers.
      executionProviders: (persistentGpuIo ? ["webgpu"] : executionProviders) as never,
      graphOptimizationLevel: "all",
    });
    const inputName = session.inputNames[0];
    const outputName = session.outputNames[0];
    // The raster is static in the export (`dynamic: False`), but read it off the graph
    // rather than trusting a constant: exporting at 416 instead of 640 is the documented
    // escape hatch when the CPU budget is tight, and a re-export at another size must not
    // silently produce corners in the wrong places. Named dimensions come back as
    // strings, so anything non-numeric is treated as absent rather than coerced.
    // The legacy WebGL execution provider can run a model but does not expose session
    // metadata. Its dedicated exports are square, fixed-raster YOLO graphs; seed those
    // known dimensions and validate the actual output with the warm-up immediately below.
    const meta = webgl ? null : session.inputMetadata[0];
    const shape = webgl
      ? ([1, 3, webglRaster, webglRaster] as ReadonlyArray<unknown>)
      : meta && !meta.isTensor
        ? []
        : ((meta?.shape ?? []) as ReadonlyArray<unknown>);
    const axis = (i: number) =>
      typeof shape[i] === "number" && (shape[i] as number) > 0 ? (shape[i] as number) : 0;
    const height = shape.length === 4 ? axis(2) : 0;
    const width = shape.length === 4 ? axis(3) : 0;
    if (!height || !width) {
      throw new Error(
        `detector.onnx has no static input raster (input shape ${JSON.stringify(shape)})`,
      );
    }
    const webglRows =
      webgl && width % 32 === 0 ? (width / 8) ** 2 + (width / 16) ** 2 + (width / 32) ** 2 : 0;
    const outputMeta = webgl ? null : session.outputMetadata[0];
    const outputShape = webgl
      ? ([1, webglRows, 4 + 1 + 3 * KEYPOINTS] as ReadonlyArray<unknown>)
      : outputMeta && !outputMeta.isTensor
        ? []
        : ((outputMeta?.shape ?? []) as ReadonlyArray<unknown>);
    const outputAxis = (i: number) =>
      typeof outputShape[i] === "number" && (outputShape[i] as number) > 0
        ? (outputShape[i] as number)
        : 0;
    const candidateRows = outputShape.length === 3 && outputAxis(0) === 1 ? outputAxis(1) : 0;
    const outputStride = outputShape.length === 3 ? outputAxis(2) : 0;
    const candidateLayout = outputStride === 4 + 1 + 3 * KEYPOINTS;
    const endToEndLayout = outputStride === 4 + 1 + 1 + 3 * KEYPOINTS;
    if (!candidateRows || (!candidateLayout && !endToEndLayout)) {
      await session.release();
      throw new Error(
        `detector.onnx is not a ${KEYPOINTS}-keypoint pose graph ` +
          `(output shape ${JSON.stringify(outputShape)})`,
      );
    }

    let gpuIo: CapturedGpuIo | null = null;
    if (persistentGpuIo) {
      const device = (await ort.env.webgpu.device) as GPUDevice;
      const inputBytes = 3 * width * height * Float32Array.BYTES_PER_ELEMENT;
      const outputElements = candidateRows * outputStride;
      const outputBytes = outputElements * Float32Array.BYTES_PER_ELEMENT;
      const inputBuffer = device.createBuffer({
        label: "card detector input",
        size: align16(inputBytes),
        usage: GPU_STORAGE | GPU_COPY_DST,
      });
      const outputBuffer = device.createBuffer({
        label: "card detector output",
        size: align16(outputBytes),
        usage: GPU_STORAGE | GPU_COPY_SRC | GPU_COPY_DST,
      });
      const readbackBuffer = device.createBuffer({
        label: "card detector readback",
        size: align16(outputBytes),
        usage: GPU_MAP_READ | GPU_COPY_DST,
      });
      gpuIo = {
        device,
        inputBuffer,
        outputBuffer,
        readbackBuffer,
        inputTensor: ort.Tensor.fromGpuBuffer(inputBuffer as OrtNamespace.Tensor.GpuBufferType, {
          dataType: "float32",
          dims: [1, 3, height, width],
        }),
        outputTensor: ort.Tensor.fromGpuBuffer(outputBuffer as OrtNamespace.Tensor.GpuBufferType, {
          dataType: "float32",
          dims: [1, candidateRows, outputStride],
        }),
        outputData: new Float32Array(outputElements),
        outputBytes,
      };
    }

    const detector = new PoseDetector(
      session,
      inputName,
      outputName,
      width,
      height,
      outputStride,
      candidateLayout ? 5 : 6,
      candidateLayout ? 300 : candidateRows,
      gpuIo,
    );

    // Pay the warm-up here rather than inside the first live frame, where it would read
    // as a stutter the moment the camera opens. WebGPU keeps the same external buffers;
    // WebGL2 fills its fixed-shape texture pool once and reuses it thereafter.
    if (gpuIo) {
      gpuIo.device.queue.writeBuffer(gpuIo.inputBuffer, 0, detector.input);
      const result = await session.run(
        { [inputName]: gpuIo.inputTensor },
        { [outputName]: gpuIo.outputTensor },
      );
      if (result[outputName] !== gpuIo.outputTensor) {
        throw new Error("detector did not retain its preallocated WebGPU output");
      }
      await detector.readGpuOutput();
      // A comparison flag keeps the old path available for real-device measurements.
      if (!new URLSearchParams(location.search).has("cpudetector")) {
        detector.videoSampler = await GpuLetterbox.create(
          gpuIo.device,
          gpuIo.inputBuffer,
          width,
          height,
        );
      }
      return detector;
    }

    const probeInput = new ort.Tensor("float32", new Float32Array(3 * width * height), [
      1,
      3,
      height,
      width,
    ]);
    let probe: OrtNamespace.InferenceSession.ReturnType | null = null;
    try {
      probe = await session.run({ [inputName]: probeInput });
      const dims = probe[outputName].dims;
      if (dims.join("x") !== [1, candidateRows, outputStride].join("x"))
        throw new Error(`detector output changed from metadata (${dims.join("x")})`);
    } finally {
      disposeTensors(probeInput, ...Object.values(probe ?? {}));
    }
    return detector;
  }

  /**
   * Quads in *source* pixels — the space the letterbox was built from — best first.
   */
  async detect(
    ort: typeof OrtNamespace,
    frame: LetterboxedFrame,
    confidence = CONFIDENCE,
  ): Promise<PoseDetection[]> {
    const { scale, left, top } = frame;
    const packedAt = performance.now();
    if (!frame.gpu) this.pack(frame.data);
    const inferAt = performance.now();
    this.lastPackMs = frame.gpu ? 0 : inferAt - packedAt;
    this.lastQueueMs = 0;

    if (this.gpuIo) {
      const raw = await withOrtLock(async () => {
        this.lastQueueMs = performance.now() - inferAt;
        const io = this.gpuIo!;
        if (!frame.gpu) io.device.queue.writeBuffer(io.inputBuffer, 0, this.input);
        const result = await this.session.run(
          { [this.inputName]: io.inputTensor },
          { [this.outputName]: io.outputTensor },
        );
        if (result[this.outputName] !== io.outputTensor) {
          throw new Error("detector replaced its preallocated WebGPU output");
        }
        return this.readGpuOutput();
      });
      this.lastInferMs = performance.now() - inferAt;
      return this.decode(raw, scale, left, top, confidence);
    }

    const input = new ort.Tensor("float32", this.input, [1, 3, this.height, this.width]);
    let outputs: OrtNamespace.InferenceSession.ReturnType | null = null;
    try {
      // Serialised with the embedder's runs — see ort-lock.ts; concurrent WebGPU runs
      // corrupt onnxruntime's heap.
      outputs = await withOrtLock(() => {
        this.lastQueueMs = performance.now() - inferAt;
        return this.session.run({ [this.inputName]: input });
      });
      const raw = outputs[this.outputName].data as Float32Array;
      this.lastInferMs = performance.now() - inferAt;
      return this.decode(raw, scale, left, top, confidence);
    } finally {
      disposeTensors(input, ...Object.values(outputs ?? {}));
    }
  }

  letterboxVideo(
    video: PixelSource,
    window: SourceWindow,
    area: ViewRect,
  ): Promise<LetterboxedFrame | null> {
    return this.videoSampler?.prepare(video, window, area) ?? Promise.resolve(null);
  }

  gpuThumbnail(frame: LetterboxedFrame, quad: Float32Array, area: ViewRect): Promise<Float32Array> {
    if (!this.videoSampler) throw new Error("WebGPU thumbnail unavailable");
    return this.videoSampler.thumbnail(frame, quad, area);
  }

  releaseVideo(): void {
    this.videoSampler?.releaseVideo();
  }

  /** Copy the persistent WebGPU output into one reusable CPU array. */
  private async readGpuOutput(): Promise<Float32Array> {
    const io = this.gpuIo;
    if (!io) throw new Error("detector has no WebGPU IO buffers");
    const encoder = io.device.createCommandEncoder({ label: "card detector readback" });
    encoder.copyBufferToBuffer(io.outputBuffer, 0, io.readbackBuffer, 0, io.outputBytes);
    io.device.queue.submit([encoder.finish()]);
    await io.readbackBuffer.mapAsync(MAP_MODE_READ, 0, io.outputBytes);
    try {
      const mapped = io.readbackBuffer.getMappedRange(0, io.outputBytes);
      io.outputData.set(new Float32Array(mapped, 0, io.outputData.length));
    } finally {
      io.readbackBuffer.unmap();
    }
    return io.outputData;
  }

  /** Decode either the WebGPU candidate rows or the older ranked end-to-end rows. */
  private decode(
    raw: Float32Array,
    scale: number,
    left: number,
    top: number,
    confidence: number,
  ): PoseDetection[] {
    const slots = raw.length / this.outputStride;
    const found: PoseDetection[] = [];
    for (let i = 0; i < slots; i++) {
      const base = i * this.outputStride;
      const score = raw[base + 4];
      // Two floors, deliberately distinct: the tiny prefilter avoids constructing quads
      // for empty candidate rows, while `confidence` is the actual detection policy.
      if (score <= PREFILTER_SCORE || score < confidence) continue;

      const quad = new Float32Array(8);
      for (let k = 0; k < KEYPOINTS; k++) {
        const at = base + this.keypointOffset + k * 3;
        quad[k * 2] = (raw[at] - left) / scale;
        quad[k * 2 + 1] = (raw[at + 1] - top) / scale;
      }
      // The corner *labels* came from the synthetic generator's ordering; re-derive them
      // geometrically so a card photographed at 45 degrees still warps the right way up.
      const ordered = orderCorners(quad);
      const [w, h] = edgeLengths(ordered);
      found.push({
        quad: ordered,
        area: contourArea(ordered),
        aspect: h > 0 ? w / h : 0,
        score,
        source: "model",
      });
    }
    found.sort((a, b) => b.score - a.score);
    return found.length > this.maxCandidates ? found.slice(0, this.maxCandidates) : found;
  }

  /**
   * Interleaved RGBA at model size to the graph's planar RGB float input.
   *
   * The padding is already in `data` — the canvas drew it — so this is one pass with no
   * branches and no intermediate buffer. It writes into a Float32Array owned by the
   * detector rather than allocating 4.9 MB per frame.
   */
  private pack(data: Uint8ClampedArray): void {
    const plane = this.width * this.height;
    const input = this.input;
    const unit = 1 / 255;
    let i = 0;
    let s = 0;
    // Four pixels per iteration gives Safari fewer loop/index checks in this CPU-side
    // upload pass. All shipped rasters are multiples of 32; the tail keeps the helper
    // correct for a future fixed raster that is not.
    for (; i + 3 < plane; i += 4, s += 16) {
      input[i] = data[s] * unit;
      input[i + 1] = data[s + 4] * unit;
      input[i + 2] = data[s + 8] * unit;
      input[i + 3] = data[s + 12] * unit;
      input[plane + i] = data[s + 1] * unit;
      input[plane + i + 1] = data[s + 5] * unit;
      input[plane + i + 2] = data[s + 9] * unit;
      input[plane + i + 3] = data[s + 13] * unit;
      input[2 * plane + i] = data[s + 2] * unit;
      input[2 * plane + i + 1] = data[s + 6] * unit;
      input[2 * plane + i + 2] = data[s + 10] * unit;
      input[2 * plane + i + 3] = data[s + 14] * unit;
    }
    for (; i < plane; i++, s += 4) {
      input[i] = data[s] * unit;
      input[plane + i] = data[s + 1] * unit;
      input[2 * plane + i] = data[s + 2] * unit;
    }
  }
}
